import { FileIcon } from 'lucide-react';

import {
  MENTION_ROWS_PER_SOURCE,
  type MentionEntry,
} from '@/ui/chat/composer/mention-entry.js';

import type { WorkspaceArtifact } from '@/lib/api/workspace-artifacts.js';

import { formatBytes, typeLabel } from './artifact-display.js';

/**
 * The workspace's own files as rows of the composer's `@` popup.
 *
 * Matching is a filter over the list the client already holds — the same substring match the "Add
 * artifacts" dialog's search box does, so the same typing finds the same file through either door — and it
 * costs no request at all, which is why these rows are on screen before SharePoint has answered.
 *
 * The row says the type and the size, as the dialog's rows do, and the accessible name says ARTIFACT: the
 * popup lists SharePoint files right beside these, and a file name repeated across the two sources is
 * ordinary.
 */
export function artifactMentionEntries(
  artifacts: WorkspaceArtifact[],
  query: string,
  select: (artifact: WorkspaceArtifact) => void,
): MentionEntry[] {
  const needle = query.trim().toLowerCase();
  return artifacts
    .filter((artifact) => artifact.name.toLowerCase().includes(needle))
    .slice(0, MENTION_ROWS_PER_SOURCE)
    .map((artifact) => {
      const detail = `${typeLabel(artifact.contentType, artifact.name)} · ${formatBytes(artifact.size)}`;
      return {
        key: `artifact:${artifact.id}`,
        kind: 'artifact' as const,
        icon: FileIcon,
        label: artifact.name,
        detail,
        accessibleName: `Artifact ${artifact.name}, ${detail}`,
        select: () => select(artifact),
      };
    });
}
